import { dayjsInTz, dayKeyInTz } from "../dayjs";
import {
  APP_DEFAULT_TZ,
  getCompanyTimezone,
  resolveClockingTimezone,
} from "./teamTimezone";

/**
 * Start/end of calendar day in an IANA zone → epoch ms (inclusive end).
 * @param {string|undefined|null} tz
 * @param {any} [value] dayjs-compatible instant or calendar hint
 */
export function dayBoundsInTz(tz, value) {
  const zone = typeof tz === "string" && tz.trim() ? tz.trim() : APP_DEFAULT_TZ;
  const base =
    value === undefined || value === null
      ? dayjsInTz(zone)
      : dayjsInTz(zone, value);
  return {
    startMs: base.startOf("day").valueOf(),
    endMs: base.endOf("day").valueOf(),
  };
}

/**
 * Start/end of calendar month in an IANA zone → epoch ms.
 * @param {string|undefined|null} tz
 * @param {any} [value]
 */
export function monthBoundsInTz(tz, value) {
  const zone = typeof tz === "string" && tz.trim() ? tz.trim() : APP_DEFAULT_TZ;
  const base =
    value === undefined || value === null
      ? dayjsInTz(zone)
      : dayjsInTz(zone, value);
  return {
    startMs: base.startOf("month").valueOf(),
    endMs: base.endOf("month").valueOf(),
  };
}

/**
 * Ant Design / dayjs picker value → YYYY-MM-DD in company (or given) TZ.
 * Uses calendar Y/M/D from the picker wall, then interprets in `tz`.
 * @param {any} pickerValue
 * @param {string} [tz]
 */
export function pickerToDayKey(pickerValue, tz = getCompanyTimezone()) {
  if (pickerValue == null) return null;
  const zone = typeof tz === "string" && tz.trim() ? tz.trim() : APP_DEFAULT_TZ;

  if (typeof pickerValue?.format === "function") {
    const y = pickerValue.year?.() ?? Number(pickerValue.format("YYYY"));
    const m = pickerValue.month?.() ?? Number(pickerValue.format("M")) - 1;
    const d = pickerValue.date?.() ?? Number(pickerValue.format("D"));
    if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
      return dayjsInTz(zone)
        .year(y)
        .month(m)
        .date(d)
        .format("YYYY-MM-DD");
    }
  }

  return dayKeyInTz(zone, pickerValue);
}

/**
 * Picker value → day start/end ms in company (or given) TZ.
 * @param {any} pickerValue
 * @param {string} [tz]
 */
export function pickerToDayBounds(pickerValue, tz = getCompanyTimezone()) {
  const key = pickerToDayKey(pickerValue, tz);
  if (!key) return null;
  const zone = typeof tz === "string" && tz.trim() ? tz.trim() : APP_DEFAULT_TZ;
  const base = dayjsInTz(zone, key);
  return {
    startMs: base.startOf("day").valueOf(),
    endMs: base.endOf("day").valueOf(),
    dayKey: key,
  };
}

/**
 * Calendar day key (YYYY-MM-DD) for a clocking row using snapshot → team → default.
 * @param {{ timezone?: string, employeeTeam?: string, clockInDate?: number }|null|undefined} clocking
 * @param {Array|undefined} programFields
 * @param {string|undefined|null} [employeeTeam]
 */
export function clockingDayKey(clocking, programFields, employeeTeam) {
  const tz = resolveClockingTimezone(clocking, employeeTeam, programFields);
  const instant = clocking?.clockInDate;
  if (instant == null) return null;
  return dayKeyInTz(tz, instant);
}

/**
 * Display day key DD/MM/YYYY in the clocking's resolved TZ.
 */
export function clockingDayDisplayKey(clocking, programFields, employeeTeam) {
  const tz = resolveClockingTimezone(clocking, employeeTeam, programFields);
  const instant = clocking?.clockInDate;
  if (instant == null) return null;
  return dayjsInTz(tz, instant).format("DD/MM/YYYY");
}

export { getCompanyTimezone, APP_DEFAULT_TZ };
